import React, { useEffect } from "react";
import { scrollToBooking } from "../utils/scrollToSection";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  MusicNote as MusicIcon,
  Event as EventIcon,
} from "@mui/icons-material";
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import BookingForm from "../components/bookingForm";

import weddingReception from "../assets/dhol-players-london-natural-history-museum.webp";

const AsianaBridalShow = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Asiana Lifestyle Show | Imperial Drummers</title>
        <meta name="description" content="Meet Imperial Drummers at the Asiana Lifestyle Show. Book premium dhol players for your wedding entertainment." />
        <link rel="canonical" href="https://imperialdrummers.com/exhibitions" />
        <meta property="og:title" content="Asiana Lifestyle Show | Imperial Drummers" />
        <meta property="og:description" content="Meet Imperial Drummers at the Asiana Lifestyle Show. Book premium dhol players for your wedding entertainment." />
        <meta property="og:url" content="https://imperialdrummers.com/exhibitions" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Navbar />
      <HeroSection />
      <WhyChooseSection />

      {/* Booking Form */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: '#f8f5fb' }}>
        <BookingForm />
      </Box>

      <Footer />
    </>
  );
};

// ── Hero Section ──────────────────────────────────────────────
const HeroSection = () => (
  <Box sx={{
    position: 'relative',
    height: { xs: '80vh', md: '85vh' },
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    backgroundImage: `url(${weddingReception})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: { md: 'fixed' }
  }}>
    {/* Overlay */}
    <Box sx={{
      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
      background: 'linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.45) 30%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0.8) 100%)',
      zIndex: 1
    }} />
    {/* Vignette */}
    <Box sx={{
      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
      boxShadow: 'inset 0 0 150px rgba(0,0,0,0.5)',
      zIndex: 1, pointerEvents: 'none'
    }} />

    {/* Title pinned to top */}
    <Container maxWidth="lg" sx={{ textAlign: 'center', zIndex: 2, position: 'absolute', top: 0, left: 0, right: 0, pt: { xs: 12, md: 14 } }}>
      <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
        <Box className="hero-stagger hero-stagger-up" sx={{ animationDuration: '0.5s', animationDelay: '0s' }}>
          <Typography variant="overline" sx={{
            color: '#ffd700', fontWeight: 700, letterSpacing: '3px', mb: 1, display: 'block',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)', fontSize: { xs: '0.75rem', md: '0.9rem' }
          }}>
            Asiana Lifestyle Show
          </Typography>
        </Box>

        <Box className="hero-stagger hero-stagger-up" sx={{ animationDuration: '0.5s', animationDelay: '0.1s' }}>
          <Typography variant="h1" sx={{
            color: 'white', fontWeight: 800,
            textShadow: '0 4px 12px rgba(0,0,0,0.5)',
            fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
            lineHeight: 1.1
          }}>
            Premium <span style={{ color: '#ffd700' }}>Dhol</span> Entertainment
          </Typography>
        </Box>
      </Box>
    </Container>

    {/* CTA pinned to bottom */}
    <Container maxWidth="lg" sx={{ textAlign: 'center', zIndex: 2, position: 'relative', pb: { xs: 6, md: 8 } }}>
      <Box className="hero-stagger hero-stagger-up" sx={{ animationDuration: '0.5s', animationDelay: '0.2s' }}>
        <Button
          variant="contained"
          color="gold"
          size="large"
          onClick={scrollToBooking}
          startIcon={<EventIcon />}
          sx={{
            px: 6, py: 2,
            boxShadow: '0 8px 20px rgba(255, 215, 0, 0.3)',
            '&:hover': { boxShadow: '0 12px 25px rgba(255, 215, 0, 0.4)' }
          }}
        >
          Get Your Quote
        </Button>
      </Box>
    </Container>
  </Box>
);

// ── Why Choose Section ────────────────────────────────────────
const whyChooseItems = [
  {
    icon: <CheckIcon sx={{ fontSize: 56, mb: 2, color: '#ffd700', filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.5))' }} />,
    title: "Authentic Performances",
    text: "Traditional Punjabi dhol rhythms performed by experienced musicians who understand the cultural significance of each wedding celebration."
  },
  {
    icon: <MusicIcon sx={{ fontSize: 56, mb: 2, color: '#ffd700', filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.5))' }} />,
    title: "Custom Performances",
    text: "Tailored performances for your Pre-Wedding, Baraat, Reception, and other wedding celebrations - choreographed to your specific cultural traditions and preferences."
  },
  {
    icon: <EventIcon sx={{ fontSize: 56, mb: 2, color: '#ffd700', filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.5))' }} />,
    title: "Event Coordination",
    text: "Perfect timing coordination with your wedding planners, photographers, and venue staff for seamless celebrations."
  },
];

const cardStyle = {
  height: '100%',
  background: 'linear-gradient(145deg, rgba(92, 0, 93, 0.4) 0%, rgba(45, 0, 82, 0.6) 100%)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(160, 137, 9, 0.3)',
  transition: 'all 0.4s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    border: '1px solid rgba(255, 215, 0, 0.6)',
    boxShadow: '0 12px 40px rgba(255, 215, 0, 0.2)',
    background: 'linear-gradient(145deg, rgba(92, 0, 93, 0.6) 0%, rgba(45, 0, 82, 0.8) 100%)'
  }
};

const WhyChooseSection = () => (
  <Box sx={{
    py: { xs: 6, md: 8 },
    background: 'linear-gradient(135deg, #1a0033 0%, #2d0052 50%, #1a0033 100%)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
      background: 'radial-gradient(circle at 20% 50%, rgba(160, 137, 9, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(92, 0, 93, 0.2) 0%, transparent 50%)',
      pointerEvents: 'none'
    }
  }}>
    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
      <Typography variant="h2" align="center" gutterBottom sx={{
        mb: 2, color: '#ffd700', fontWeight: 'bold',
        textShadow: '0 0 30px rgba(255, 215, 0, 0.5)',
        fontSize: { xs: '2rem', md: '3rem' }
      }}>
        Why Choose Imperial Drummers?
      </Typography>
      <Typography variant="h6" align="center" sx={{
        mb: 6, color: 'rgba(255, 255, 255, 0.9)',
        maxWidth: '700px', mx: 'auto',
        fontSize: { xs: '1rem', md: '1.15rem' }
      }}>
        The UK's premier dhol entertainment company for weddings
      </Typography>

      <Grid container spacing={4} justifyContent="center">
        {whyChooseItems.map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.title}>
            <Card sx={cardStyle}>
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                {item.icon}
                <Typography variant="h5" gutterBottom sx={{ color: '#ffd700', fontWeight: 'bold', mb: 2 }}>
                  {item.title}
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.7 }}>
                  {item.text}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

export default AsianaBridalShow;
